import { html } from "htm/preact";
import { useEffect, useState } from "preact/hooks";
import { restoreOrganiserAccess } from "../organiserAccess.js";
import { TERMS } from "../copy.js";

/**
 * Consumes an organiser recovery link (`#/t/<code>/organiser/<token>`) and restores
 * admin access on this device.
 *
 * This exists because the organiser token is issued exactly once, at creation, and
 * lives only in the creator's localStorage — so clearing Safari data, switching
 * phones, or simply not opening the app for a week (long enough for Safari to evict
 * storage from a non-installed site, spec §7) would otherwise leave a game with all
 * its scores permanently unmanageable.
 *
 * The link is one of two ways in; the other is pasting it into `RestoreAccess`, for a
 * phone whose home-screen app can't be reached by a tapped link at all. Both hand off
 * to the same `restoreOrganiserAccess`, which is where the token is checked before
 * anything is written.
 */
export function OrganiserRecovery({ code, token }: { code: string; token: string }) {
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    restoreOrganiserAccess(code, token).catch((err) => {
      if (!cancelled) setError((err as Error).message);
    });
    return () => {
      cancelled = true;
    };
  }, [code, token]);

  if (error) {
    return html`
      <h1>Link didn't work</h1>
      <p class="field-help">
        ${`We couldn't open ${TERMS.event} ${code.toUpperCase()}: ${error}.`}
      </p>
      <p class="field-help">
        If you have the link somewhere you can copy it from, you can
        <a href="#/restore">paste it in here</a> instead — which is also the way in on a
        phone where this app lives on the home screen.
      </p>
      <p><a href="#/">← Home</a></p>
    `;
  }

  return html`<p>${`Restoring ${TERMS.organizer} access…`}</p>`;
}
